/** Route rendering: a per-venue preset (routeStyles.js) drawn as a stack of
 *  MapLibre layers, plus floor-transition markers. Segments carry floor_id;
 *  setFloor() in mapLayers.js filters them.
 *
 *  Layer stack, bottom to top — created once, most of them hidden most of the
 *  time. A preset shows the ones it needs and sets `visibility: none` on the
 *  rest, so an unused layer costs nothing per frame.
 *
 *  The animation clock is deliberately the same one this file has always had:
 *  rAF runs continuously but a paint property is only written when the
 *  QUANTISED step changes. That is ~14 writes a second, not 60, and it is the
 *  reason the route is affordable on totem hardware. Every preset is held to
 *  that ceiling (see routeStyles.js).
 */
import {
  DEFAULT_COLOR,
  markSizeExpr,
  markSpacingExpr,
  palette,
  resolveRouteStyle,
  routeStyleById,
  stepFor,
  staticStepFor,
} from "./routeStyles.js";
import maplibregl from "maplibre-gl";

import { lowPower } from "../../lib/lowPower.js";

const LINE_LAYERS = ["route-glow", "route-casing", "route-rail", "route-line", "route-flow"];
const MARK_LAYER = "route-marks";
const KEY_TO_LAYER = {
  glow: "route-glow",
  casing: "route-casing",
  rail: "route-rail",
  line: "route-line",
  flow: "route-flow",
};

// Every layer the route owns, bottom to top. They move as ONE BLOCK: a route
// whose line hides behind a shop but whose chevrons, step dots and
// floor-change circles still float on the roof is worse than either choice
// (item 0169). Anything drawn FROM the route geometry is the route.
const ROUTE_LAYERS = [...LINE_LAYERS, MARK_LAYER, "route-transitions"];

// The venue's buildings. Inserting the route immediately before this layer is
// the whole of the occluded mode -- see applyRouteOrder().
const OCCLUDER_LAYER = "units";

const emptyFC = () => ({ type: "FeatureCollection", features: [] });

const reducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

let animFrame = null;

// --------------------------------------------------------------- marks ------

/** The "Flechas" glyph. Drawn here rather than shipped as an asset so it never
 *  depends on the basemap's glyph ranges — a text "▶" is not present in every
 *  font the six offered basemaps serve. White with a dark rim reads on any
 *  accent colour, so it does not need recolouring when the venue does. */
function chevronImage() {
  const S = 64;
  const c = document.createElement("canvas");
  c.width = c.height = S;
  const ctx = c.getContext("2d");
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  const draw = (stroke, w) => {
    ctx.beginPath();
    ctx.moveTo(20, 14);
    ctx.lineTo(44, 32);
    ctx.lineTo(20, 50);
    ctx.strokeStyle = stroke;
    ctx.lineWidth = w;
    ctx.stroke();
  };
  draw("rgba(10,14,20,0.85)", 13);
  draw("#ffffff", 7);
  return ctx.getImageData(0, 0, S, S);
}

/** The "Pasos" dot. Unlike the chevron this one IS recoloured — it is the route
 *  itself rather than an annotation on it, so it takes the venue's accent and
 *  the polarity-flipped casing as its rim, exactly like the line does.
 *
 *  96 px drawn at pixelRatio 3, i.e. 32 CSS px at icon-size 1: an ordinary
 *  non-SDF image, redrawn when the accent changes. The alternative was an SDF
 *  icon recoloured with icon-color/icon-halo-*, which would avoid the redraw at
 *  the cost of hand-encoding a distance field whose only consumer is one
 *  circle. A canvas is cheaper to get right and impossible to get subtly wrong.
 */
const DOT_PX = 96;
function dotImage(fill, rim) {
  const c = document.createElement("canvas");
  c.width = c.height = DOT_PX;
  const ctx = c.getContext("2d");
  const disc = (r, color) => {
    ctx.beginPath();
    ctx.arc(DOT_PX / 2, DOT_PX / 2, r, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
  };
  disc(DOT_PX / 2 - 3, rim);
  disc(DOT_PX / 2 - 13, fill);
  return ctx.getImageData(0, 0, DOT_PX, DOT_PX);
}

// The colours the dot was last drawn with, so switching floors or re-running a
// route does not rebuild an identical image.
let dotKey = null;

function ensureDotImage(map, pal) {
  const key = `${pal.accent}|${pal.casing}`;
  if (dotKey === key && map.hasImage("route-dot")) return;
  try {
    const img = dotImage(pal.accent, pal.casing);
    if (map.hasImage("route-dot")) map.updateImage("route-dot", img);
    else map.addImage("route-dot", img, { pixelRatio: 3 });
    dotKey = key;
  } catch {
    /* a missing dot degrades "Pasos" to its thin line, not to a crash */
  }
}

function ensureLayers(map) {
  if (map.getSource("route")) return;

  // lineMetrics is the whole reason "Cometa", "Onda" and "Flechas" can exist:
  // line-gradient is invalid on a source without it, and it CANNOT be added
  // after addSource — the source would have to be torn down and rebuilt.
  map.addSource("route", { type: "geojson", data: emptyFC(), lineMetrics: true });
  map.addSource("route-points", { type: "geojson", data: emptyFC() });

  if (!map.hasImage("route-chevron")) {
    try {
      map.addImage("route-chevron", chevronImage());
    } catch {
      /* a missing chevron degrades "Flechas" to a plain line, not to a crash */
    }
  }

  const line = (id, paint) => ({
    id,
    type: "line",
    source: "route",
    layout: { "line-cap": "round", "line-join": "round", visibility: "none" },
    paint,
  });

  map.addLayer(line("route-glow", { "line-color": DEFAULT_COLOR, "line-width": 16, "line-opacity": 0.3 }));
  map.addLayer(line("route-casing", { "line-color": "#ffffff", "line-width": 9, "line-opacity": 0.9 }));
  map.addLayer(line("route-rail", { "line-color": DEFAULT_COLOR, "line-width": 3 }));
  map.addLayer(line("route-line", { "line-color": DEFAULT_COLOR, "line-width": 4.5 }));
  // route-flow is the ONLY gradient layer, so no other layer ever has to have
  // a line-gradient removed when the venue switches preset (MapLibre also
  // rejects gradient and dasharray together, and every other layer dashes).
  map.addLayer(line("route-flow", { "line-width": 5, "line-opacity": 1 }));

  // ONE symbol layer for every mark-based preset. Which glyph, how big, how far
  // apart and whether it lies on the ground are all the preset's business
  // (routeStyles.js `marks`); this layer just exists.
  map.addLayer({
    id: MARK_LAYER,
    type: "symbol",
    source: "route",
    layout: {
      visibility: "none",
      "icon-image": "route-chevron",
      "icon-size": 0.8,
      "symbol-placement": "line",
      "symbol-spacing": 112,
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
      // Chevrons lie flat on the ground: the viewer is pitched 50-60°, and a
      // billboarded arrow on a tilted map points at the sky. Dots do the
      // opposite (see `align` in routeStyles.js) — a ground-aligned circle is
      // an ellipse, and the perspective that flattens it is the same one that
      // made the old marks unreadable.
      "icon-rotation-alignment": "map",
      "icon-pitch-alignment": "map",
    },
  });

  map.addLayer({
    id: "route-transitions",
    type: "circle",
    source: "route-points",
    paint: {
      "circle-radius": 9,
      "circle-color": DEFAULT_COLOR,
      "circle-stroke-color": "#ffffff",
      "circle-stroke-width": 3,
    },
  });
}

// ------------------------------------------------------------- painting -----

const PAINT = {
  color: "line-color",
  width: "line-width",
  opacity: "line-opacity",
  dash: "line-dasharray",
  offset: "line-offset",
  blur: "line-blur",
};

const toGradient = (stops) => [
  "interpolate",
  ["linear"],
  ["line-progress"],
  ...stops.flatMap(([t, c]) => [t, c]),
];

function paintLayer(map, layer, props) {
  for (const [key, value] of Object.entries(props)) {
    if (key === "gradient") map.setPaintProperty(layer, "line-gradient", toGradient(value));
    else if (PAINT[key]) map.setPaintProperty(layer, PAINT[key], value);
  }
}

/** Static setup for a preset: visibility + the properties that do not animate. */
function applyPreset(map, preset, pal) {
  const spec = preset.build(pal);

  for (const [key, layer] of Object.entries(KEY_TO_LAYER)) {
    const props = spec[key];
    map.setLayoutProperty(layer, "visibility", props ? "visible" : "none");
    if (!props) continue;
    // Reset the properties a previous preset may have left behind, so
    // switching preset is not order-dependent.
    map.setPaintProperty(layer, "line-offset", props.offset ?? 0);
    map.setPaintProperty(layer, "line-blur", props.blur ?? 0);
    if (key !== "flow") map.setPaintProperty(layer, "line-dasharray", props.dash ?? undefined);
    map.setPaintProperty(layer, "line-opacity", props.opacity ?? 1);
    map.setLayoutProperty(layer, "line-cap", props.cap ?? "round");
    paintLayer(map, layer, props);
  }

  const marks = spec.marks;
  map.setLayoutProperty(MARK_LAYER, "visibility", marks ? "visible" : "none");
  if (marks) {
    const dot = marks.shape === "dot";
    if (dot) ensureDotImage(map, pal);
    const align = marks.align ?? "map";
    map.setLayoutProperty(MARK_LAYER, "icon-image", dot ? "route-dot" : "route-chevron");
    // Zoom EXPRESSIONS, not the scalars: the preset's numbers are the values at
    // the anchor zoom, and below it the gap and the mark shrink together so the
    // route keeps its marks as it gets smaller (item 0112, MARK_ZOOM in
    // routeStyles.js). MapLibre evaluates these itself, so this is still one
    // write per preset change and nothing per frame.
    map.setLayoutProperty(MARK_LAYER, "icon-size", markSizeExpr(marks.size));
    map.setLayoutProperty(MARK_LAYER, "symbol-spacing", markSpacingExpr(marks.spacing));
    map.setLayoutProperty(MARK_LAYER, "icon-rotation-alignment", align);
    map.setLayoutProperty(MARK_LAYER, "icon-pitch-alignment", align);
  }

  // DECISION (item 0106): the floor-change circles and the "Estás aquí" marker
  // FOLLOW the accent colour. They are parts of the same wayfinding object —
  // origin, path, floor change — and a venue that brands its route green does
  // not want two unexplained red dots left over. Leaving them fixed would also
  // read as a different KIND of thing (a warning) rather than "your route
  // continues here". youAreHere.js takes the same accent for the same reason.
  map.setPaintProperty("route-transitions", "circle-color", pal.accent);
  map.setPaintProperty("route-transitions", "circle-stroke-color", preset.ring?.(pal) ?? pal.casing);
}

function applyFrame(map, preset, pal, step) {
  const f = preset.frame(step, pal);
  for (const [key, props] of Object.entries(f)) {
    const layer = KEY_TO_LAYER[key];
    if (layer && map.getLayer(layer)) paintLayer(map, layer, props);
  }
}

/** Where the route sits in the layer stack -- the ONE thing
 *  `settings.route_occluded` changes (item 0169).
 *
 *  MEASURED ON THE DEV MAP BEFORE ANY OF THIS WAS WRITTEN, because the obvious
 *  explanation is the wrong one. MapLibre does NOT depth-test a 2D line
 *  against `fill-extrusion` geometry and then decide the route wins: there is
 *  no depth test in play at all. The route is simply PAINTED LAST, because
 *  ensureLayers() runs on the first route request -- long after
 *  addVenueLayers() built `slabs`/`decorations`/`units`/`poi-symbols` and after
 *  the three.js model layer has attached itself on top of those. Last painted,
 *  on top. Nothing more exotic than that, which is why the fix is one
 *  moveLayer() per layer and not a shader.
 *
 *  So:
 *    occluded = false (DEFAULT, and every venue that has not opted in)
 *      route on top of everything, byte-for-byte today's render.
 *    occluded = true
 *      the block moves to immediately BELOW `units`, so the venue's buildings
 *      -- and the 3D models that sit above them -- hide the parts of the line
 *      that run behind them, while `slabs` and `decorations` (the ground
 *      carpet the route walks on, and the knee-high planters and trees) stay
 *      underneath it. The setting is about BUILDINGS, which is what the
 *      customer pointed at; burying the line under the paving it follows would
 *      just delete it.
 *
 *  Idempotent and re-applied on every showRoute(), not only at layer creation:
 *  the model layer loads asynchronously and can attach AFTER the first route,
 *  and a venue can be previewed with ?route_occluded= without a reload.
 *
 *  What does NOT move: the "Estás aquí" marker. It is a maplibregl.Marker --
 *  an HTML element positioned over the canvas, outside the GL layer stack
 *  entirely -- so there is no order to give it, and faking one would mean
 *  hand-raycasting the extrusions. It also answers the one question a visitor
 *  cannot recover from the map ("where am I"), so it stays visible in both
 *  modes on purpose, not by omission.
 */
function applyRouteOrder(map, occluded) {
  // No `units` layer (a venue with no buildings) means nothing to hide behind:
  // fall back to today's look rather than to an invisible route.
  const before = occluded && map.getLayer(OCCLUDER_LAYER) ? OCCLUDER_LAYER : undefined;
  for (const id of ROUTE_LAYERS) {
    // moveLayer(id) with no beforeId moves to the TOP, so the default branch
    // re-establishes the stack the route has always had, in order.
    if (map.getLayer(id)) map.moveLayer(id, before);
  }
}

// ---------------------------------------------------------------- public ----

/** Resolve a venue's route look. `override` lets the viewer honour
 *  ?route_style= / ?route_color= / ?route_occluded= for previewing a choice
 *  before saving it.
 *
 *  `occluded` is nullish-coalesced, not OR-ed: the override's whole job is to
 *  be able to preview OFF on a venue that has saved ON. */
export function routeLookFor(settings, override) {
  const base = resolveRouteStyle(settings);
  if (!override) return base;
  return {
    preset: override.style ? routeStyleById(override.style) : base.preset,
    color: override.color ?? base.color,
    occluded: override.occluded ?? base.occluded,
  };
}

export function showRoute(map, route, floorId, { compact = false, docked = false, framed = false, look } = {}) {
  ensureLayers(map);

  const preset = look?.preset ?? routeStyleById(undefined);
  const pal = palette(look?.color);

  const lines = {
    type: "FeatureCollection",
    features: route.segments.map((seg) => ({
      type: "Feature",
      geometry: seg.geometry,
      properties: { floor_id: seg.floor_id },
    })),
  };
  const points = {
    type: "FeatureCollection",
    features: route.segments
      .filter((seg) => seg.transition_out)
      .map((seg) => ({
        type: "Feature",
        geometry: { type: "Point", coordinates: seg.transition_out.at },
        properties: { floor_id: seg.floor_id, kind: seg.transition_out.kind },
      })),
  };
  map.getSource("route").setData(lines);
  map.getSource("route-points").setData(points);

  const filter = ["==", ["get", "floor_id"], floorId];
  for (const id of ROUTE_LAYERS) map.setFilter(id, filter);

  applyRouteOrder(map, look?.occluded === true);
  applyPreset(map, preset, pal);

  // Fit the first segment into view.
  //
  // Top-docked menu (item 0373): NOT here. The fit has to be computed against
  // the panel's FINAL height, and at this moment the panel is still the POI
  // card that asked for the route -- it only folds to the directions on the
  // next render. ViewerPage fits it once the dock has settled
  // (dockedRouteCamera below).
  //
  // `framed` -- the floating layout of a wide portrait screen (item 0377): the
  // same, against the floating card and directions panel once they have
  // settled (floatingRouteCamera below).
  const bounds = routeBounds(route);
  if (bounds && !docked && !framed) {
    // Padding keeps the route clear of the chrome. left:380 is the desktop POI
    // panel -- on a 390px phone that plus right:80 exceeds the canvas, MapLibre
    // logs "Map cannot fit within canvas with the given bounds, padding, and/or
    // offset" and silently does not fit at all, so the route a visitor just
    // asked for can be off-screen. On a phone the chrome is a bottom sheet, not
    // a left panel (item 0093).
    const padding = compact
      ? { top: 90, bottom: 210, left: 24, right: 24 }
      : { top: 120, bottom: 80, left: 380, right: 80 };
    map.fitBounds(bounds, { padding, maxZoom: 18.5, duration: 900 });
  }

  stopAnim();

  // Paint one frame up front. route-flow has no line-color, so a gradient
  // preset that waited for the first rAF would show a black line for a frame.
  let step = staticStepFor(preset);
  applyFrame(map, preset, pal, step);

  // prefers-reduced-motion: that representative frame is the whole render, and
  // the loop never starts. A totem runs for hours in front of people who did
  // not choose to look at it, so this is not decoration.
  //
  // Low-power mode (item 0315) does the same: an animated line keeps the map
  // redrawing ~60 times a second for as long as the route is shown, which on a
  // weak SoC is the difference between a responsive screen and a sluggish one.
  if (preset.steps <= 1 || reducedMotion() || lowPower().on) return;

  const tick = (timestamp) => {
    const next = stepFor(preset, timestamp);
    if (next !== step) {
      step = next;
      if (map.getLayer("route-line")) applyFrame(map, preset, pal, step);
    }
    animFrame = requestAnimationFrame(tick);
  };
  animFrame = requestAnimationFrame(tick);
}

/** The box the route fit frames: the first segment, the one drawn first.
 *  [[w, s], [e, n]], or null for a route with no geometry. */
export function routeBounds(route) {
  const coords = route?.segments?.[0]?.geometry?.coordinates;
  if (!coords?.length) return null;
  const lngs = coords.map((c) => c[0]);
  const lats = coords.map((c) => c[1]);
  return [
    [Math.min(...lngs), Math.min(...lats)],
    [Math.max(...lngs), Math.max(...lats)],
  ];
}

const ROUTE_MAX_ZOOM = 18.5;
// What stands ABOVE or beside a point on screen, in px, and has to be in view
// with it. The "You are here" badge sits on the route's first point (~105px
// wide, its top ~57px up). A POI marker is a disc on its point with the name
// under it, raised by its building's height in 3D (item 0371).
const YAH_BOX = { left: 56, right: 56, up: 62, down: 4 };
const DEST_BOX = { left: 48, right: 48, up: 48, down: 44 };

/** Camera that frames a route in the map left visible under a top-docked
 *  panel whose bottom edge is at `padTop` (item 0373), keeping `margin` clear
 *  of the edges and the controls -- to be applied in ONE easeTo together with
 *  `padding: { top: padTop }`.
 *
 *  Not map.cameraForBounds(), for two reasons measured on Marina Port Premia:
 *  it frames against the map's CURRENT padding (the expanded POI card's, at
 *  the moment "Show route" is tapped), and it ignores pitch, so at the venue's
 *  50 degrees the near half of a route spills past the margins. So this starts
 *  from the flat estimate and corrects it on a copy of the map's own transform
 *  -- the real projection, pitch and padding included -- until the route, the
 *  "You are here" badge and the destination marker all sit inside the frame.
 *  North up, as fitBounds always left a route. */
export function dockedRouteCamera(map, route, padTop, margin) {
  const box = map.getContainer();
  return routeCameraInFrame(map, route, padTop, {
    x0: margin.left,
    x1: box.clientWidth - margin.right,
    y0: padTop + margin.top,
    y1: box.clientHeight - margin.bottom,
  });
}

/** The same solver for an explicit `frame` ({x0, x1, y0, y1}, px from the
 *  canvas's top-left): dockedRouteCamera's frame is "under the dock, inside
 *  the margins"; floatingRouteCamera's is the free area beside and below the
 *  floating card and directions panel. */
export function routeCameraInFrame(map, route, padTop, frame) {
  const bounds = routeBounds(route);
  if (!bounds) return null;
  const box = map.getContainer();
  const w = box.clientWidth;
  const h = box.clientHeight;

  // Flat estimate: MapLibre's cameraForBounds maths, against padTop.
  const sw = maplibregl.MercatorCoordinate.fromLngLat(bounds[0]);
  const ne = maplibregl.MercatorCoordinate.fromLngLat(bounds[1]);
  const spanX = Math.max(ne.x - sw.x, 1e-12) * 512;
  const spanY = Math.max(sw.y - ne.y, 1e-12) * 512;
  const clampZoom = (z) => Math.max(map.getMinZoom(), Math.min(z, ROUTE_MAX_ZOOM, map.getMaxZoom()));
  const flat = (f) => {
    const availW = f.x1 - f.x0;
    const availH = f.y1 - f.y0;
    if (availW <= 0 || availH <= 0) return null;
    const zoom = clampZoom(Math.log2(Math.min(availW / spanX, availH / spanY)));
    // Box centre at the frame's centre, i.e. offset from the padded centre
    // ((padTop + h) / 2, w / 2) by the difference of the margins.
    const ws = 512 * 2 ** zoom;
    const cx = ((sw.x + ne.x) / 2) * ws - ((f.x0 + f.x1) / 2 - w / 2);
    const cy = ((sw.y + ne.y) / 2) * ws - ((f.y0 + f.y1) / 2 - (padTop + h) / 2);
    const c = new maplibregl.MercatorCoordinate(cx / ws, cy / ws).toLngLat();
    return { center: [c.lng, c.lat], zoom, bearing: 0 };
  };
  const estimate = flat(frame);
  // If even the margins do not fit, frame with none rather than not at all.
  if (!estimate) return flat({ x0: 0, x1: w, y0: padTop, y1: h });
  if (typeof map.transform?.clone !== "function") return estimate;

  // What has to be in view: every point of the line, plus the boxes around
  // its start and the destination (when the destination is on this floor).
  const coords = route.segments[0].geometry.coordinates;
  const items = coords.map((c) => ({ at: c, box: null }));
  items[0].box = YAH_BOX;
  const poi = route.poi;
  if (poi && Number.isFinite(poi.lng) && Number.isFinite(poi.lat) && poi.floor_id === route.segments[0].floor_id) {
    items.push({ at: [poi.lng, poi.lat], box: DEST_BOX });
  }

  const tr = map.transform.clone();
  // The maths runs unfenced; routeFence() then admits wherever it lands.
  tr.setConstrainOverride((lngLat, zoom) => ({ center: maplibregl.LngLat.convert(lngLat), zoom }));
  tr.setPadding({ top: padTop, bottom: 0, left: 0, right: 0 });
  tr.setBearing(0);
  let zoom = estimate.zoom;
  let center = maplibregl.LngLat.convert(estimate.center);
  for (let i = 0; i < 10; i++) {
    tr.setZoom(zoom);
    tr.setCenter(center);
    let x0 = Infinity;
    let x1 = -Infinity;
    let y0 = Infinity;
    let y1 = -Infinity;
    for (const it of items) {
      const p = tr.locationToScreenPoint(maplibregl.LngLat.convert(it.at));
      const b = it.box ?? { left: 0, right: 0, up: 0, down: 0 };
      x0 = Math.min(x0, p.x - b.left);
      x1 = Math.max(x1, p.x + b.right);
      y0 = Math.min(y0, p.y - b.up);
      y1 = Math.max(y1, p.y + b.down);
    }
    const scale = Math.min((frame.x1 - frame.x0) / Math.max(x1 - x0, 1), (frame.y1 - frame.y0) / Math.max(y1 - y0, 1));
    const dx = (frame.x0 + frame.x1) / 2 - (x0 + x1) / 2;
    const dy = (frame.y0 + frame.y1) / 2 - (y0 + y1) / 2;
    const nextZoom = clampZoom(zoom + Math.log2(scale));
    if (Math.abs(dx) < 1 && Math.abs(dy) < 1 && Math.abs(nextZoom - zoom) < 0.01) break;
    // Slide what is in view so its box is centred on the frame, then zoom
    // about the centre; repeat, because under pitch neither is linear.
    const cp = tr.centerPoint;
    center = tr.screenPointToLocation(new maplibregl.Point(cp.x - dx, cp.y - dy));
    zoom = nextZoom;
  }
  if (!Number.isFinite(zoom) || !Number.isFinite(center.lng) || !Number.isFinite(center.lat)) return estimate;
  return { center: [center.lng, center.lat], zoom, bearing: 0 };
}

/** Camera for a route in the FLOATING layout of a portrait screen at least
 *  820px wide -- the Port Premia totem (1080x1920, and 1080x1350 as the iframe
 *  inside its sitummap page) since f0e189a gave it back the floating card.
 *
 *  There the POI card floats top-left and the directions panel top-right, and
 *  the old fitBounds padding ({top:120, left:380, right:80}) knew neither: the
 *  directions panel is ~370px tall with its steps open, and the Premia totem
 *  stands at the venue's north-east edge, so "You are here" and the start of
 *  every route landed under it (QA round 2 on item 0377).
 *
 *  `obstacles` are the panels' MEASURED rects ({left, top, right, bottom}, px
 *  from the canvas's top-left). The frame is the free area left of them: below
 *  all of them, or beside one and below whatever overlaps that column. Each
 *  candidate is solved like the docked case, and the one that shows the route
 *  largest wins (ties: the bigger frame). `margin` keeps clear of the screen
 *  edges and the controls: the zoom/compass/floor column on the right, the
 *  language picker, Reset view and the "Ask me" / QR row along the bottom.
 *
 *  Returns null when nothing fits; the caller then leaves the camera alone. */
export function floatingRouteCamera(map, route, obstacles, margin) {
  const box = map.getContainer();
  const w = box.clientWidth;
  const h = box.clientHeight;
  const L = margin.left;
  const R = w - margin.right;
  const y1 = h - margin.bottom;
  const gap = margin.gap;
  const columns = [[L, R]];
  for (const o of obstacles) {
    columns.push([o.right + gap, R], [L, o.left - gap]);
  }
  const MIN = 160;
  let best = null;
  for (const [x0, x1] of columns) {
    if (x1 - x0 < MIN) continue;
    // Below every panel that reaches into this column.
    let y0 = margin.top;
    for (const o of obstacles) {
      if (o.right > x0 && o.left < x1) y0 = Math.max(y0, o.bottom + gap);
    }
    if (y1 - y0 < MIN) continue;
    const frame = { x0, x1, y0, y1 };
    const cam = routeCameraInFrame(map, route, 0, frame);
    if (!cam) continue;
    const area = (x1 - x0) * (y1 - y0);
    if (
      !best ||
      cam.zoom > best.cam.zoom + 0.05 ||
      (Math.abs(cam.zoom - best.cam.zoom) <= 0.05 && area > best.area)
    ) {
      best = { cam, frame, area };
    }
  }
  return best;
}

/** The venue fence (settings.max_bounds) while a route is shown under a
 *  top-docked panel (item 0373). A MapLibre transformConstrain.
 *
 *  The stock fence keeps the WHOLE canvas inside max_bounds and knows nothing
 *  of padding or pitch. With the panel over the top of the canvas the route
 *  has to be centred in the strip below it, which pushes the canvas top well
 *  past the venue -- and the fence pulled the camera back, leaving the route
 *  under the panel. On Marina Port Premia the totem itself is north of
 *  max_bounds, so EVERY route started under it; the bottom sheet never hit
 *  this because it left the route in the top half of the screen.
 *
 *  So while a route is shown, only the camera's CENTRE is fenced, to
 *  max_bounds widened to take in the route and the point it is framed on. The
 *  visitor still cannot wander off the venue; they can see their route. */
export function routeFence(map, bounds, center) {
  const fence = map.getMaxBounds();
  if (!fence) return null;
  const w = Math.min(fence.getWest(), bounds[0][0], center[0]);
  const s = Math.min(fence.getSouth(), bounds[0][1], center[1]);
  const e = Math.max(fence.getEast(), bounds[1][0], center[0]);
  const n = Math.max(fence.getNorth(), bounds[1][1], center[1]);
  return (lngLat, zoom) => {
    const c = maplibregl.LngLat.convert(lngLat);
    return {
      center: new maplibregl.LngLat(Math.min(Math.max(c.lng, w), e), Math.min(Math.max(c.lat, s), n)),
      zoom: Math.min(Math.max(+zoom, map.getMinZoom()), map.getMaxZoom()),
    };
  };
}

function stopAnim() {
  if (animFrame !== null) {
    cancelAnimationFrame(animFrame);
    animFrame = null;
  }
}

export function clearRoute(map) {
  stopAnim();
  if (map.getSource("route")) {
    map.getSource("route").setData(emptyFC());
    map.getSource("route-points").setData(emptyFC());
  }
}
